import type { DragEvent } from "react";
import { Copy, FilePlus2, Pencil, Trash2 } from "lucide-react";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { composerBus } from "@/lib/composerBus";
import { blueprintToBriefDocument } from "@/lib/composerPushDocs";
import {
  BLOCK_TYPE_LABELS,
  blueprintToPlainText,
  resolveBlueprintBlockRef,
  type ComposerBlock,
  type ComposerBlueprint,
} from "@/lib/composerBlocks";

const handleDragStart =
  (bp: ComposerBlueprint, blocks: ComposerBlock[]) => (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData("text/plain", blueprintToPlainText(bp, blocks));
    e.dataTransfer.setData(
      "application/x-excerptus-blueprint",
      JSON.stringify({ id: bp.id, title: bp.title, blockRefs: bp.blockRefs }),
    );
    e.dataTransfer.effectAllowed = "copy";
  };

type Props = {
  bp: ComposerBlueprint;
  blocks: ComposerBlock[];
  mode: "catalog" | "drag";
  onEdit: (bp: ComposerBlueprint) => void;
  onDuplicate: (bp: ComposerBlueprint) => void;
  duplicatePending: boolean;
  onDelete: (id: string) => void;
};

export const BlueprintListItem = ({
  bp,
  blocks,
  mode,
  onEdit,
  onDuplicate,
  duplicatePending,
  onDelete,
}: Props) => (
  <AccordionItem key={bp.id} value={bp.id}>
    <AccordionTrigger className="py-2 text-sm">
      <div className="flex w-full items-center gap-2 pr-2">
        <span className="font-medium">{bp.title}</span>
        <Badge variant="outline" className="text-[10px] tabular-nums">
          {bp.blockRefs.length}
        </Badge>
      </div>
    </AccordionTrigger>
    <AccordionContent>
      {bp.description && (
        <p className="mb-2 text-xs text-muted-foreground">{bp.description}</p>
      )}
      <div
        draggable={mode === "drag"}
        onDragStart={mode === "drag" ? handleDragStart(bp, blocks) : undefined}
        className={
          "rounded-md border border-border/60 bg-card p-2 " +
          (mode === "drag"
            ? "cursor-grab hover:border-primary/60 hover:bg-accent/40 active:cursor-grabbing"
            : "")
        }
      >
        <ol className="space-y-1 text-[11px]">
          {bp.blockRefs.map((ref, idx) => {
            const block = resolveBlueprintBlockRef(ref, blocks);
            return (
              <li key={`${bp.id}-${idx}`} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-muted text-[10px] font-medium tabular-nums text-muted-foreground">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  <span className="font-medium">{BLOCK_TYPE_LABELS[ref.type]}</span>
                  {block ? (
                    <span className="text-muted-foreground"> · {block.title}</span>
                  ) : ref.note ? (
                    <span className="text-muted-foreground"> · {ref.note}</span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      {mode === "catalog" && (
        <div className="mt-2 flex items-center justify-end gap-1">
          <Button
            size="sm"
            onClick={() => {
              const doc = blueprintToBriefDocument(bp, blocks);
              composerBus.emit("draft:insert", {
                doc,
                mode: "replace-clean",
                source: `blueprint:${bp.id}`,
                label: bp.title,
              });
            }}
          >
            <FilePlus2 className="h-3.5 w-3.5" />
            Push to Draft Canvas
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(bp)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDuplicate(bp)}
            disabled={duplicatePending}
            title="Save as a new blueprint"
          >
            <Copy className="h-3.5 w-3.5" />
            Duplicate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (confirm(`Delete blueprint "${bp.title}"?`)) onDelete(bp.id);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      )}
    </AccordionContent>
  </AccordionItem>
);
